/**
 * One place that decides how urgent a subscription's state is.
 *
 * Two surfaces read this and must never disagree: the compact chip in the top
 * bar (next to the user name) and the full-width banner. Deriving the severity
 * twice is how they would drift, so the thresholds live here only.
 */
import { Subscription } from "../types/subscription";
import { formatNumber } from "../config/plans";

export type Severity = "info" | "warning" | "severe" | "danger";

export interface SubscriptionState {
  severity: Severity | null;
  /** Short form for the top bar — a few words, no sentences. */
  chipLabel: string;
  /** Where clicking the chip should take the user. */
  chipTo: string;
  /** True only when moderation has actually stopped: the one state loud enough
   *  to earn a full-width banner rather than a chip. */
  isBlocking: boolean;
}

export const getSubscriptionState = (
  subscription: Subscription
): SubscriptionState => {
  const { usage, status, daysRemaining, trialStarted, moderationPaused } = subscription;
  const comments = usage.comments;
  const percent = comments.percent;
  const hasLimit = comments.limit !== null;

  if (moderationPaused) {
    return {
      severity: "danger",
      chipLabel: "Moderation stopped",
      chipTo: "/pricing",
      isBlocking: true,
    };
  }

  if (!trialStarted && status === "trial") {
    return {
      severity: "info",
      chipLabel: "Trial not started",
      chipTo: "/accounts",
      isBlocking: false,
    };
  }

  if (percent >= 90 && hasLimit) {
    const left = Math.max(0, (comments.limit as number) - comments.used);
    return {
      severity: "severe",
      chipLabel: `${formatNumber(left)} comments left`,
      chipTo: "/pricing",
      isBlocking: false,
    };
  }

  if (percent >= 80 && hasLimit) {
    return {
      severity: "warning",
      chipLabel: `${percent}% of comments used`,
      chipTo: "/pricing",
      isBlocking: false,
    };
  }

  if (status === "trial" && trialStarted) {
    // Healthy trial: the only time-critical fact is how long is left. Usage
    // numbers, dates and the meter live on Settings > Billing.
    const days =
      daysRemaining !== null
        ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`
        : "";
    return {
      severity: "info",
      chipLabel: ["Free trial", days].filter(Boolean).join(" · "),
      chipTo: "/settings",
      isBlocking: false,
    };
  }

  // Healthy paid plan under 80% — say nothing at all.
  return { severity: null, chipLabel: "", chipTo: "/settings", isBlocking: false };
};
